import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import NoteIcon from '@mui/icons-material/Note';
import LayersIcon from '@mui/icons-material/Layers';
import { keyframes } from '@mui/system';

/**
 * EventTicker - Displays real-time events scrolling across the screen
 *
 * Shows a thin horizontal bar with:
 * - Scrolling event cards when events arrive (right to left)
 * - Pulsing green indicator when idle
 */

// Keyframes for scrolling animation (right to left) - each card animates independently
const scrollCard = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100vw);
  }
`;

// Keyframes for pulsing animation
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
`;

const EVENT_TYPE_CONFIG = {
  match: {
    icon: PersonIcon,
    color: '#4caf50',
    label: 'Match'
  },
  multi_dataset_match: {
    icon: LayersIcon,
    color: '#9c27b0',
    label: 'Multi-Dataset'
  },
  assignment: {
    icon: AssignmentIcon,
    color: '#ff9800',
    label: 'Assignment'
  },
  group_match: {
    icon: GroupIcon,
    color: '#2196f3',
    label: 'Group'
  },
  note: {
    icon: NoteIcon,
    color: '#fbc02d',
    label: 'Note'
  }
};

export default function EventTicker({ newEvents = [] }) {
  const [displayEvents, setDisplayEvents] = useState([]);
  const eventIdRef = useRef(new Set());
  const eventCountRef = useRef(0); // Track total events added for spacing

  // Add new events to the ticker - accumulate events in scrolling panel
  useEffect(() => {
    if (newEvents.length === 0) return;

    console.log('[EventTicker] Received new events:', newEvents.length, newEvents.map(e => `${e.eventType}:${e.eventId?.substring(0, 10)}`));

    // Filter out events we've already shown
    const uniqueNewEvents = newEvents.filter(event => {
      if (eventIdRef.current.has(event.eventId)) {
        console.log('[EventTicker] Skipping duplicate:', event.eventId.substring(0, 15));
        return false;
      }
      eventIdRef.current.add(event.eventId);
      return true;
    });

    if (uniqueNewEvents.length === 0) {
      console.log('[EventTicker] No unique events to display');
      return;
    }

    console.log('[EventTicker] Adding', uniqueNewEvents.length, 'unique events to ticker:', uniqueNewEvents.map(e => `${e.eventType}:${e.eventId?.substring(0, 10)}`));

    // Add the new event to the scrolling panel with spacing offset
    const event = uniqueNewEvents[0];
    const timestamp = Date.now();
    const eventIndex = eventCountRef.current++;
    const eventWithKey = {
      ...event,
      tickerKey: `${event.eventId}-${timestamp}`,
      spacingOffset: eventIndex % 3 // Stagger by 0, 1, 2 for spacing
    };

    console.log('[EventTicker] Adding event to panel:', `${eventWithKey.eventType}:${eventWithKey.tickerKey}`, 'offset:', eventWithKey.spacingOffset);

    // Add to end of current events (append to scrolling panel)
    setDisplayEvents(prev => [...prev, eventWithKey]);

    // Remove this specific event after it scrolls off screen (8 seconds matches animation)
    const timer = setTimeout(() => {
      setDisplayEvents(prev => {
        const filtered = prev.filter(e => e.tickerKey !== eventWithKey.tickerKey);
        console.log('[EventTicker] Cleanup - removing event:', eventWithKey.tickerKey, 'remaining:', filtered.length);
        return filtered;
      });
    }, 8000);

    return () => clearTimeout(timer);
  }, [newEvents]);

  // Cleanup old event IDs from ref (prevent memory leak)
  useEffect(() => {
    const cleanup = setInterval(() => {
      if (eventIdRef.current.size > 100) {
        eventIdRef.current = new Set(
          Array.from(eventIdRef.current).slice(-50)
        );
      }
    }, 60000); // Every minute

    return () => clearInterval(cleanup);
  }, []);

  const getEventSummary = (event) => {
    const config = EVENT_TYPE_CONFIG[event.eventType] || EVENT_TYPE_CONFIG.match;
    const Icon = config.icon;

    let primaryText = '';
    let secondaryText = '';

    switch (event.eventType) {
      case 'match':
        primaryText = event.metadata?.personName || 'Unknown';
        secondaryText = '';
        break;
      case 'multi_dataset_match':
        const datasetCount = event.metadata?.datasetCount || event.metadata?.datasets?.length || 0;
        primaryText = event.metadata?.personName || 'Unknown';
        secondaryText = `${datasetCount} datasets`;
        break;
      case 'assignment':
        primaryText = event.description || 'Assignment';
        secondaryText = event.status || '';
        break;
      case 'group_match':
        const matchCount = event.metadata?.matchCount || 0;
        primaryText = `${matchCount} ${matchCount === 1 ? 'match' : 'matches'}`;
        secondaryText = '';
        break;
      case 'note':
        primaryText = event.description || 'Note';
        secondaryText = '';
        break;
      default:
        primaryText = event.eventType;
    }

    return { Icon, config, primaryText, secondaryText };
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 22.5,
        minHeight: 22.5,
        maxHeight: 22.5,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Idle Indicator - Pulsing Green Circle */}
      {displayEvents.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              animation: `${pulse} 2s ease-in-out infinite`
            }}
          />
        </Box>
      )}

      {/* Scrolling Event Cards - container scrolls as a train */}
      {displayEvents.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            paddingRight: 2
          }}
        >
          {displayEvents.map((event) => {
            const { Icon, config, primaryText, secondaryText } = getEventSummary(event);

            return (
              <Paper
                key={event.tickerKey}
                elevation={2}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 16px', // 30% larger padding
                  backgroundColor: 'white',
                  borderLeft: `4px solid ${config.color}`,
                  minWidth: 260, // 30% larger than 200
                  flexShrink: 0,
                  height: 57, // 30% larger than 44
                  animation: `${scrollCard} 8s linear`,
                  animationFillMode: 'forwards'
                }}
              >
                <Icon sx={{ color: config.color, fontSize: 16 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    sx={{ lineHeight: 1.2 }}
                  >
                    {primaryText}
                  </Typography>
                  {secondaryText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ lineHeight: 1.2 }}
                    >
                      {secondaryText}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    backgroundColor: config.color,
                    color: 'white'
                  }}
                />
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
